import { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useFocusManager } from "react-aria";

import { FOCUSABLE } from "./useSubpanelKeyboardNav";

/**
 * W3C APG Disclosure Navigation — optional keyboard support for the
 * main-menu bar (menu items + search trigger).
 *
 * Must be called inside a <FocusScope> so useFocusManager() works.
 *
 * Right/Left always navigate between triggers.
 * Down moves to next trigger — unless the trigger's panel is open
 * (pass `openPanelId`), in which case Down focuses the first
 * focusable element inside the panel instead
 * (see issue w3c/aria-practices#1632).
 * Up moves to previous trigger.
 * Home/End jump to first/last trigger.
 *
 * Submenu panels are inline in the DOM (for correct Tab order), but their
 * focusable elements are visible to the parent FocusScope's useFocusManager
 * because nested FocusScopes don't isolate from the parent's DOM walker
 * (see adobe/react-spectrum#2817, #3226). The `accept` filter restricts
 * arrow-key navigation to elements marked with data-menu-trigger so it
 * stays on the menubar.
 */
const accept = (node: Element) => node.hasAttribute("data-menu-trigger");

export function useMainMenuKeyboardNav(openPanelId?: string) {
    const focusManager = useFocusManager();

    const onKeyDown = (event: ReactKeyboardEvent) => {
        /* v8 ignore if -- @preserve */
        if (!focusManager) return;

        // When this trigger's panel is open, ArrowDown focuses into
        // the panel instead of moving to the next menu-bar trigger.
        if (openPanelId && event.key === "ArrowDown") {
            event.preventDefault();
            const panel = document.getElementById(openPanelId);
            /* v8 ignore else -- @preserve */
            if (panel) {
                const firstFocusable =
                    panel.querySelector<HTMLElement>(FOCUSABLE);
                firstFocusable?.focus();
            }
            return;
        }

        switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
                event.preventDefault();
                focusManager.focusNext({ wrap: true, accept });
                break;
            case "ArrowLeft":
            case "ArrowUp":
                event.preventDefault();
                focusManager.focusPrevious({ wrap: true, accept });
                break;
            case "Home":
                event.preventDefault();
                focusManager.focusFirst({ accept });
                break;
            case "End":
                event.preventDefault();
                focusManager.focusLast({ accept });
                break;
        }
    };

    return { onKeyDown };
}
